import type { VoidFunctionComponent } from 'react';

import Subnav from '../../generic/Subnav';
import SubnavCountBadge from '../../generic/Subnav/SubnavCountBadge';

import { useInventoryDependencyCounts } from './hooks/useInventoryDependencyCounts';
import { useInventoryDropdownFeatures } from './hooks/useInventoryDropdownFeatures';
import type { InventoryDependencyCounts, InventoryDependencyIsLoading, InventoryScope } from './types';

type InventorySubnavDropdownProps = {
  baseUrl: string;
  isLoading: InventoryDependencyIsLoading;
  counts: InventoryDependencyCounts;
  shouldShowBinaryAnalysis: boolean;
  shouldShowSnippets: boolean;
  shouldShowVendored: boolean;
};

const InventorySubnavDropdown: VoidFunctionComponent<InventorySubnavDropdownProps> = ({
  baseUrl,
  isLoading,
  counts,
  shouldShowBinaryAnalysis,
  shouldShowSnippets,
  shouldShowVendored,
}) => (
  <Subnav.Dropdown to={`${baseUrl}/browse/dependencies`} navKey="inventory">
    <Subnav.Dropdown.RootItem>
      Inventory
      <SubnavCountBadge
        count={counts.total}
        isLoading={isLoading.any}
      />
    </Subnav.Dropdown.RootItem>
    <Subnav.Dropdown.Item to={`${baseUrl}/browse/dependencies`} navKey="browse/dependencies">
      Dependencies
      <SubnavCountBadge count={counts.managed || 0} isLoading={isLoading.managed} />
    </Subnav.Dropdown.Item>
    {/* Snippets are only available for project scope */}
    {shouldShowSnippets && (
      <Subnav.Dropdown.Item to={`${baseUrl}/browse/snippets`} navKey="snippets">
        Snippets
        <SubnavCountBadge count={counts.snippets || 0} isLoading={isLoading.snippets} />
      </Subnav.Dropdown.Item>
    )}
    {shouldShowBinaryAnalysis && (
      <Subnav.Dropdown.Item to={`${baseUrl}/browse/binary`} navKey="binary">
        Binary Analysis
        <SubnavCountBadge count={counts.binary || 0} isLoading={isLoading.binary} />
      </Subnav.Dropdown.Item>
    )}
    {shouldShowVendored && (
      <Subnav.Dropdown.Item to={`${baseUrl}/browse/vendored`} navKey="vendored">
        Vendored
        <SubnavCountBadge count={counts.vendored || 0} isLoading={isLoading.vendored} />
      </Subnav.Dropdown.Item>
    )}
  </Subnav.Dropdown>
);

type Props = {
  /**
   * The base URL for either this project revision, or release group release.
   */
  baseUrl: string;
  /**
   * The scope of the inventory dropdown, used to determine which features should be shown.
   */
  scope: InventoryScope;
  /**
   * This is used to hold off loading the counts until the
   * parent data has loaded.
   *
   * In the project scope, this means waiting until the revision data has loaded,
   * since we want it to be loaded first in the waterfall.
   *
   * For release group scope, this means waiting until the release group data exists.
   */
  isLoading: boolean;
};

/**
 * InventorySubnavItem contains the subnav item for the "Inventory" section.
 * For orgs where Snippet Scanning is not enabled, this is basically just the old Dependencies subnav item.
 * For orgs where Snippet Scanning is enabled, this is the Inventory dropdown.
 */
const InventorySubnavItem: VoidFunctionComponent<Props> = ({ baseUrl, scope, isLoading: isLoadingParentData }) => {
  const features = useInventoryDropdownFeatures(scope);
  const { isLoading, counts } = useInventoryDependencyCounts(scope, features, !isLoadingParentData);
  const {
    shouldShowInventory,
    shouldShowSnippets,
    shouldShowBinaryAnalysis,
    shouldShowVendored,
  } = features;

  //  Just show the one Dependencies subnav item if the inventory dropdown is not enabled
  if (!shouldShowInventory) {
    return (
      <Subnav.ItemWithCount
        to={`${baseUrl}/browse/dependencies`}
        navKey="browse/dependencies"
        count={counts.managed ?? 0}
        isLoading={isLoading.managed}
      >
        Dependencies
      </Subnav.ItemWithCount>
    );
  }

  return (
    <InventorySubnavDropdown
      baseUrl={baseUrl}
      isLoading={isLoading}
      counts={counts}
      shouldShowSnippets={shouldShowSnippets}
      shouldShowBinaryAnalysis={shouldShowBinaryAnalysis}
      shouldShowVendored={shouldShowVendored}
    />
  );
};

export default InventorySubnavItem;
